import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Megaphone, LayoutDashboard, ListTree, Sparkles, Newspaper, Star, BookOpen, Plus, Pencil, Trash2, X, FileText, Award, Calendar, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useApp, ArticleInput, MustReadInput } from '../store';
import { navItems } from '../data';
const tabs = [{
  id: 'general',
  label: 'Homepage',
  icon: Sparkles
}, {
  id: 'news',
  label: 'News',
  icon: Newspaper
}, {
  id: 'featured',
  label: 'Featured',
  icon: Star
}, {
  id: 'mustreads',
  label: 'Must Reads',
  icon: BookOpen
}, {
  id: 'announcement',
  label: 'Announcement',
  icon: Megaphone
}, {
  id: 'modules',
  label: 'Modules',
  icon: LayoutDashboard
}, {
  id: 'navigation',
  label: 'Navigation',
  icon: ListTree
}] as const;
type TabId = (typeof tabs)[number]['id'];
// Icon choices for Must Read cards
const iconChoices = [{
  key: 'FileText',
  label: 'Document',
  icon: FileText
}, {
  key: 'Award',
  label: 'Award',
  icon: Award
}, {
  key: 'BookOpen',
  label: 'Guide',
  icon: BookOpen
}, {
  key: 'Calendar',
  label: 'Event',
  icon: Calendar
}, {
  key: 'ShieldCheck',
  label: 'Compliance',
  icon: ShieldCheck
}];
function iconToKey(icon: any): string {
  const found = iconChoices.find(c => c.icon === icon);
  return found ? found.key : 'FileText';
}
function keyToIcon(key: string): any {
  const found = iconChoices.find(c => c.key === key);
  return found ? found.icon : FileText;
}
const inputCls = 'w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900';
function Toggle({
  checked,
  onChange,
  label,
  description
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}) {
  return <div className="flex items-center justify-between py-3.5 border-b border-navy-50 last:border-0" data-id="element-245" data-mp="7fgo25cw864v">
      <div className="pr-4" data-id="element-246" data-mp="bfheyph6i9st">
        <p className="text-sm font-semibold text-navy-900" data-id="element-247" data-mp="g80sjmfv94xm">{label}</p>
        <p className="text-xs text-navy-400" data-id="element-248" data-mp="g8siohqp2agi">{description}</p>
      </div>
      <button onClick={onChange} role="switch" aria-checked={checked} aria-label={label} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-gold-500' : 'bg-navy-200'}`} data-id="element-249" data-mp="l5shc7l7saxl">
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} data-id="element-250" data-mp="3tmx1mm8zqu5r" />
      </button>
    </div>;
}
function Field({
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}) {
  return <label className="block" data-id="element-251" data-mp="2ycs4q832amf9">
      <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-252" data-mp="2yd1sdp0mwhhs">
        {label}
      </span>
      {multiline ? <textarea value={value} rows={rows} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`${inputCls} resize-none`} data-id="element-253" data-mp="2oni8cg16qhlc" /> : <input type="text" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-254" data-mp="2fo3m5ss1wt6n" />}
    </label>;
}
function SaveRow({
  saved,
  onSave
}: {
  saved: boolean;
  onSave: () => void;
}) {
  return <div className="flex items-center gap-3 pt-2" data-id="element-255" data-mp="20rcts3gsohqj">
      <button onClick={onSave} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-256" data-mp="3xt04luzlu1m">
        Save changes
      </button>
      {saved && <span className="inline-flex items-center gap-1 text-sm font-semibold text-sage" data-id="element-257" data-mp="6z4h8647tww7">
          <Check className="w-4 h-4" data-id="element-258" data-mp="bz0i3whm987j" /> Saved &amp; published
        </span>}
    </div>;
}
export function AdminPage() {
  const app = useApp();
  const {
    config,
    updateConfig
  } = app;
  const [tab, setTab] = useState<TabId>('general');
  const [saved, setSaved] = useState(false);
  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <>
      <PageHeader title="Site Administration" subtitle="Publish news, feature stories, and configure the intranet for everyone at Independence Home Loans." data-id="element-259" data-mp="3p5iue2unu2tj" />

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 max-w-4xl" data-id="element-260" data-mp="3ff7erj1m656t">
        {/* Tabs */}
        <nav className="flex md:flex-col gap-1 overflow-x-auto" data-id="element-261" data-mp="31fogrj2ioa88">
          {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${active ? 'bg-navy-600 text-white' : 'text-navy-600 hover:bg-white'}`} data-id="element-262" data-mp="2msptm0ap01zb">
                <Icon className="w-4 h-4" data-id="element-263" data-mp="2qtt6cdxg1hwb" /> {t.label}
              </button>;
        })}
        </nav>

        {/* Panel */}
        <motion.div key={tab} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-264" data-mp="35q0prc112faj">
          {tab === 'general' && <div className="space-y-4" data-id="element-265" data-mp="3aj3mft13q4uy">
              <div data-id="element-266" data-mp="3ohtjj8nx84jb">
                <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-267" data-mp="3oha2rva0bm6v">
                  Welcome banner
                </h3>
                <p className="text-sm text-navy-400" data-id="element-268" data-mp="3jktd2wn5szro">
                  The greeting shown at the top of the home dashboard.
                </p>
              </div>
              <Field label="Eyebrow label" value={config.welcome.eyebrow} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              eyebrow: v
            }
          })} data-id="element-269" data-mp="2l6e4lrt48m5m" />
              <Field label="Heading" value={config.welcome.heading} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              heading: v
            }
          })} data-id="element-270" data-mp="2zyeieqoad2z0" />
              <Field label="Supporting text" multiline value={config.welcome.subtext} onChange={v => updateConfig({
            welcome: {
              ...config.welcome,
              subtext: v
            }
          })} data-id="element-271" data-mp="1ejs9n44zqoiw" />
              <SaveRow saved={saved} onSave={flash} data-id="element-272" data-mp="127y6uahhqequ" />
            </div>}

          {tab === 'news' && <NewsManager data-id="element-273" data-mp="xa67wwifhsd5" />}
          {tab === 'featured' && <FeaturedManager data-id="element-274" data-mp="x7bnh4zf07jq" />}
          {tab === 'mustreads' && <MustReadsManager data-id="element-275" data-mp="1uxf0fbguk1on" />}

          {tab === 'announcement' && <div className="space-y-4" data-id="element-276" data-mp="1pybs9i5klfcg">
              <div data-id="element-277" data-mp="1pzdhcqjf82d6">
                <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-278" data-mp="1pyv9sxrb61py">
                  Site-wide announcement
                </h3>
                <p className="text-sm text-navy-400" data-id="element-279" data-mp="1gxnmtbu8snb6">
                  A banner shown across the top of every page.
                </p>
              </div>
              <Toggle checked={config.announcement.enabled} onChange={() => updateConfig({
            announcement: {
              ...config.announcement,
              enabled: !config.announcement.enabled
            }
          })} label="Show announcement banner" description="Display the banner to all employees." data-id="element-280" data-mp="9l56vubxdjhw" />
              <Field label="Message" multiline value={config.announcement.message} onChange={v => updateConfig({
            announcement: {
              ...config.announcement,
              message: v
            }
          })} data-id="element-281" data-mp="20m6kx6jtgx9x" />
              <div data-id="element-282" data-mp="2jj7ruwwxl7gi">
                <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-283" data-mp="2jiofrwtypdae">
                  Style
                </span>
                <div className="flex gap-2" data-id="element-284" data-mp="3c129bk89md3o">
                  {(['gold', 'navy', 'urgent'] as const).map(tone => <button key={tone} onClick={() => updateConfig({
                announcement: {
                  ...config.announcement,
                  tone
                }
              })} className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-all ${config.announcement.tone === tone ? 'ring-2 ring-gold-400' : 'ring-1 ring-navy-100'} ${tone === 'urgent' ? 'bg-red-600 text-white' : tone === 'gold' ? 'bg-gold-500 text-navy-900' : 'bg-navy-600 text-white'}`} data-id="element-285" data-mp="3leas8wnye9yq">
                      {tone === 'urgent' ? 'Emergency' : tone}
                    </button>)}
                </div>
                <p className="text-xs text-navy-400 mt-2" data-id="element-286" data-mp="rmvl7bljnl77">
                  Use{' '}
                  <span className="font-semibold text-red-600" data-id="element-287" data-mp="mokc5332peq1">Emergency</span>{' '}
                  only for urgent, time-sensitive alerts — it shows a red,
                  attention-grabbing banner on every page.
                </p>
              </div>
              <SaveRow saved={saved} onSave={flash} data-id="element-288" data-mp="3s9j9zz02doxa" />
            </div>}

          {tab === 'modules' && <div data-id="element-289" data-mp="3nazou0taoh5z">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-290" data-mp="3n9evgeipv49p">
                Homepage modules
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-291" data-mp="3ja5x0wcqei7t">
                Turn dashboard sections on or off for everyone.
              </p>
              <Toggle checked={config.modules.mustReads} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              mustReads: !config.modules.mustReads
            }
          })} label="Must Reads" description="Company must-read cards on the home dashboard." data-id="element-292" data-mp="39af7l9312tel" />
              <Toggle checked={config.modules.rateWatch} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              rateWatch: !config.modules.rateWatch
            }
          })} label="Rate Watch widget" description="The mortgage rate watch card in the right rail." data-id="element-293" data-mp="1az57da23r138" />
              <Toggle checked={config.modules.featuredAutoplay} onChange={() => updateConfig({
            modules: {
              ...config.modules,
              featuredAutoplay: !config.modules.featuredAutoplay
            }
          })} label="Auto-rotate featured news" description="Cycle the home carousel automatically." data-id="element-294" data-mp="w50eqy7hw0us" />
            </div>}

          {tab === 'navigation' && <div data-id="element-295" data-mp="1mu38mf9gp2ku">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-296" data-mp="1mul2x6ckd6q4">
                Sidebar navigation
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-297" data-mp="21qtz1z15r3yb">
                Choose which destinations appear in the sidebar. Home is always
                visible.
              </p>
              {navItems.filter(item => item.label !== 'Home').map(item => <NavToggle key={item.label} label={item.label} data-id="element-298" data-mp="144wpir4ra8yi" />)}
            </div>}
        </motion.div>
      </div>
    </>;
}
function NavToggle({
  label
}: {
  label: string;
}) {
  const {
    config,
    toggleNavItem
  } = useApp();
  const visible = !config.hiddenNav.includes(label);
  return <Toggle checked={visible} onChange={() => toggleNavItem(label)} label={label} description={visible ? 'Visible in sidebar' : 'Hidden from sidebar'} data-id="element-299" data-mp="qaceoxau4rs1" />;
}
/* ------------------------------ News manager ----------------------------- */
const emptyArticle: ArticleInput = {
  category: '',
  title: '',
  excerpt: '',
  author: '',
  authorAvatar: 'https://i.pravatar.cc/120?img=32',
  readTime: '3 min read',
  image: '',
  body: [],
  needsReview: false
};
function NewsManager() {
  const {
    articles,
    addArticle,
    updateArticle,
    removeArticle
  } = useApp();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<ArticleInput & {
    bodyText: string;
  }>({
    ...emptyArticle,
    bodyText: ''
  });
  const startNew = () => {
    setForm({
      ...emptyArticle,
      bodyText: ''
    });
    setEditing('new');
  };
  const startEdit = (id: string) => {
    const a = articles.find(x => x.id === id);
    if (!a) return;
    setForm({
      category: a.category,
      title: a.title,
      excerpt: a.excerpt,
      author: a.author,
      authorAvatar: a.authorAvatar,
      readTime: a.readTime,
      image: a.image,
      body: a.body,
      bodyText: a.body.join('\n\n'),
      needsReview: a.needsReview
    });
    setEditing(id);
  };
  const save = () => {
    const input: ArticleInput = {
      category: form.category || 'Company News',
      title: form.title || 'Untitled',
      excerpt: form.excerpt,
      author: form.author || 'Communications',
      authorAvatar: form.authorAvatar,
      readTime: form.readTime || '3 min read',
      image: form.image,
      body: form.bodyText.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean),
      needsReview: form.needsReview
    };
    if (editing === 'new') addArticle(input);else if (editing) updateArticle(editing, input);
    setEditing(null);
  };
  if (editing) {
    return <div className="space-y-4" data-id="element-300" data-mp="1d6eck1daor31">
        <div className="flex items-center justify-between" data-id="element-301" data-mp="1d38l4fh71d45">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-302" data-mp="192okv31bptds">
            {editing === 'new' ? 'New article' : 'Edit article'}
          </h3>
          <button onClick={() => setEditing(null)} className="p-1.5 text-navy-400 hover:bg-navy-50 rounded-lg" aria-label="Cancel" data-id="element-303" data-mp="3kpa110gj5dp8">
            <X className="w-5 h-5" data-id="element-304" data-mp="3tqghj9j6ioih" />
          </button>
        </div>
        <Field label="Title" value={form.title} onChange={v => setForm(f => ({
        ...f,
        title: v
      }))} data-id="element-305" data-mp="2e5d6od4dxag" />
        <div className="grid sm:grid-cols-2 gap-4" data-id="element-306" data-mp="c9yns27a3gpl">
          <Field label="Category" value={form.category} placeholder="Company News" onChange={v => setForm(f => ({
          ...f,
          category: v
        }))} data-id="element-307" data-mp="gcvaa3zxvwyt" />
          <Field label="Author" value={form.author} onChange={v => setForm(f => ({
          ...f,
          author: v
        }))} data-id="element-308" data-mp="2i6cjvd7mr7w7" />
          <Field label="Read time" value={form.readTime} placeholder="3 min read" onChange={v => setForm(f => ({
          ...f,
          readTime: v
        }))} data-id="element-309" data-mp="3lff8ntmix9lr" />
          <Field label="Image URL" value={form.image} placeholder="https://…" onChange={v => setForm(f => ({
          ...f,
          image: v
        }))} data-id="element-310" data-mp="3ui6mg02vghgk" />
        </div>
        <div className="pt-2" data-id="element-311" data-mp="lz8t5zh0f1x8">
          <Toggle checked={!!form.needsReview} onChange={() => setForm(f => ({
          ...f,
          needsReview: !f.needsReview
        }))} label="Flag for review (Stale content)" description="Adds a warning banner that this article may be out of date." data-id="element-312" data-mp="hyf4v4znnl3t" />
        </div>
        <Field label="Excerpt" multiline rows={2} value={form.excerpt} onChange={v => setForm(f => ({
        ...f,
        excerpt: v
      }))} data-id="element-313" data-mp="1aiu0s5x8zz7n" />
        <Field label="Body (separate paragraphs with a blank line)" multiline rows={7} value={form.bodyText} onChange={v => setForm(f => ({
        ...f,
        bodyText: v
      }))} data-id="element-314" data-mp="15kskfsr7idzv" />
        <div className="flex gap-3 pt-1" data-id="element-315" data-mp="hx0x7u44ogjt">
          <button onClick={save} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-316" data-mp="hy2jyvuiarhd">
            {editing === 'new' ? 'Publish article' : 'Save article'}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-navy-600 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-317" data-mp="cw3ijribu4n2">
            Cancel
          </button>
        </div>
      </div>;
  }
  return <div data-id="element-318" data-mp="jdoq2d7nevem">
      <div className="flex items-center justify-between mb-4" data-id="element-319" data-mp="jdga2cg8b9z7">
        <div data-id="element-320" data-mp="efn7dte4wy3o">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-321" data-mp="efwr4z8l690x">News</h3>
          <p className="text-sm text-navy-400" data-id="element-322" data-mp="adtdc3yg2sch">
            {articles.length} article{articles.length === 1 ? '' : 's'}{' '}
            published.
          </p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-323" data-mp="5l071bnbvbya">
          <Plus className="w-4 h-4" data-id="element-324" data-mp="3rsgm5wz2q81a" /> New
        </button>
      </div>
      <ul className="divide-y divide-navy-50" data-id="element-325" data-mp="3muop6ojknvq8">
        {articles.map(a => <li key={a.id} className="flex items-center gap-3 py-3" data-id="element-326" data-mp="3iuwg8aq9nnkc">
            <img src={a.image} alt="" className="w-14 h-10 rounded object-cover shrink-0 bg-navy-50" data-id="element-327" data-mp="1yc3t0zbvaf06" />
            <div className="min-w-0 flex-1" data-id="element-328" data-mp="22bcp4audqoyw">
              <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-329" data-mp="27bqxxv3t22xz">
                {a.title}
              </p>
              <p className="text-xs text-navy-400" data-id="element-330" data-mp="2caaknxq83ire">
                {a.category} · {a.date}
              </p>
            </div>
            <button onClick={() => startEdit(a.id)} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label={`Edit ${a.title}`} data-id="element-331" data-mp="1epejgmjgcw5m">
              <Pencil className="w-4 h-4" data-id="element-332" data-mp="1okg71dy2xq63" />
            </button>
            <button onClick={() => removeArticle(a.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Delete ${a.title}`} data-id="element-333" data-mp="1tcr5ecd693wj">
              <Trash2 className="w-4 h-4" data-id="element-334" data-mp="10ulw51xbmboh" />
            </button>
          </li>)}
      </ul>
    </div>;
}
/* ---------------------------- Featured manager --------------------------- */
const accents = ['navy', 'gold', 'sage', 'periwinkle', 'clay'];
function FeaturedManager() {
  const {
    featured,
    articles,
    addFeatured,
    updateFeaturedAccent,
    removeFeatured
  } = useApp();
  const available = articles.filter(a => !featured.some(f => f.id === a.id));
  const [selected, setSelected] = useState('');
  const [accent, setAccent] = useState('navy');
  const add = () => {
    if (!selected) return;
    addFeatured(selected, accent);
    setSelected('');
  };
  return <div data-id="element-335" data-mp="np9jfmbr4f">
      <div className="mb-4" data-id="element-336" data-mp="3w58swb8omure">
        <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-337" data-mp="3w4yxu98s5xeu">
          Featured stories
        </h3>
        <p className="text-sm text-navy-400" data-id="element-338" data-mp="33i5tzi03gi0m">
          Promote existing news articles to the home carousel.
        </p>
      </div>

      {/* Add */}
      <div className="bg-navy-50 rounded-xl p-4 mb-5 space-y-3" data-id="element-339" data-mp="38fxuv1ipvegy">
        <label className="block" data-id="element-340" data-mp="1pmmm4tm1kd08">
          <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-341" data-mp="1plkzdrvny22o">
            Feature an article
          </span>
          <select value={selected} onChange={e => setSelected(e.target.value)} className={inputCls} data-id="element-342" data-mp="1gl6bqlv88aw0">
            <option value="" data-id="element-343" data-mp="1bltpa289p5ty">Select an article…</option>
            {available.map(a => <option key={a.id} value={a.id} data-id="element-344" data-mp="16o1xd87mhjpr">
                {a.title}
              </option>)}
          </select>
        </label>
        <div className="flex items-end gap-3" data-id="element-345" data-mp="xuyegke7kenq">
          <div className="flex-1" data-id="element-346" data-mp="xvqa1v7t2ce0">
            <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-347" data-mp="xsu8zy0nv7lc">
              Accent
            </span>
            <div className="flex gap-1.5" data-id="element-348" data-mp="nx0sk3jtmtya">
              {accents.map(a => <button key={a} onClick={() => setAccent(a)} aria-label={a} className={`w-7 h-7 rounded-full capitalize text-[0] transition-all ${accent === a ? 'ring-2 ring-offset-2 ring-navy-400' : ''} ${a === 'navy' ? 'bg-navy-600' : a === 'gold' ? 'bg-gold-500' : `bg-${a}`}`} style={['sage', 'periwinkle', 'clay'].includes(a) ? {
              backgroundColor: a === 'sage' ? '#57a187' : a === 'periwinkle' ? '#6c7fbe' : '#ac7758'
            } : undefined} data-id="element-349" data-mp="ixxytf9aka5m">
                  {a}
                </button>)}
            </div>
          </div>
          <button onClick={add} disabled={!selected} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-350" data-mp="1aycuf8sy6sz5">
            <Plus className="w-4 h-4" data-id="element-351" data-mp="2vgf1vcu23jr8" /> Feature
          </button>
        </div>
        {available.length === 0 && <p className="text-xs text-navy-400" data-id="element-352" data-mp="3a5tb8kxmu16m">
            All articles are already featured. Add more in the News tab.
          </p>}
      </div>

      {/* List */}
      <ul className="divide-y divide-navy-50" data-id="element-353" data-mp="2hgvw2qopwki3">
        {featured.map(f => <li key={f.id} className="flex items-center gap-3 py-3" data-id="element-354" data-mp="2cj427r4iiob9">
            <img src={f.image} alt="" className="w-14 h-10 rounded object-cover shrink-0 bg-navy-50" data-id="element-355" data-mp="2ckyob8rismgx" />
            <div className="min-w-0 flex-1" data-id="element-356" data-mp="2mfqomkchlnfe">
              <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-357" data-mp="2mj4n3hi15chk">
                {f.title}
              </p>
              <p className="text-xs text-navy-400" data-id="element-358" data-mp="1qt2nadzxl9a">{f.category}</p>
            </div>
            <select value={f.accent} onChange={e => updateFeaturedAccent(f.id, e.target.value)} className="text-xs rounded-lg bg-navy-50 border border-transparent px-2 py-1.5 text-navy-700 capitalize focus:outline-none focus:ring-2 focus:ring-gold-400/30" aria-label="Accent color" data-id="element-359" data-mp="27etgntleinzk">
              {accents.map(a => <option key={a} value={a} data-id="element-360" data-mp="2lc8bxeyc5pzg">
                  {a}
                </option>)}
            </select>
            <button onClick={() => removeFeatured(f.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Remove ${f.title} from featured`} data-id="element-361" data-mp="2vc8x76frmg6a">
              <Trash2 className="w-4 h-4" data-id="element-362" data-mp="343stllwxkdy3" />
            </button>
          </li>)}
        {featured.length === 0 && <li className="py-6 text-center text-sm text-navy-400" data-id="element-363" data-mp="3922odveqtcj6">
            No featured stories yet.
          </li>}
      </ul>
    </div>;
}
/* --------------------------- Must Reads manager -------------------------- */
function MustReadsManager() {
  const {
    mustReads,
    addMustRead,
    updateMustRead,
    removeMustRead
  } = useApp();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState({
    title: '',
    category: '',
    due: '',
    iconKey: 'FileText'
  });
  const startNew = () => {
    setForm({
      title: '',
      category: '',
      due: '',
      iconKey: 'FileText'
    });
    setEditing('new');
  };
  const startEdit = (id: string) => {
    const m = mustReads.find(x => x.id === id);
    if (!m) return;
    setForm({
      title: m.title,
      category: m.category,
      due: m.due,
      iconKey: iconToKey(m.icon)
    });
    setEditing(id);
  };
  const save = () => {
    const input: MustReadInput = {
      title: form.title || 'Untitled',
      category: form.category || 'General',
      due: form.due || 'Read soon',
      icon: keyToIcon(form.iconKey)
    };
    if (editing === 'new') addMustRead(input);else if (editing) updateMustRead(editing, input);
    setEditing(null);
  };
  if (editing) {
    return <div className="space-y-4" data-id="element-364" data-mp="2o0ux3gt2mff3">
        <div className="flex items-center justify-between" data-id="element-365" data-mp="29anp5v0owesn">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-366" data-mp="2981ehmii5j67">
            {editing === 'new' ? 'New must-read' : 'Edit must-read'}
          </h3>
          <button onClick={() => setEditing(null)} className="p-1.5 text-navy-400 hover:bg-navy-50 rounded-lg" aria-label="Cancel" data-id="element-367" data-mp="208ecozlv2bcm">
            <X className="w-5 h-5" data-id="element-368" data-mp="1qbrtef96nscp" />
          </button>
        </div>
        <Field label="Title" value={form.title} onChange={v => setForm(f => ({
        ...f,
        title: v
      }))} data-id="element-369" data-mp="1qdmim5rhm6ml" />
        <div className="grid sm:grid-cols-2 gap-4" data-id="element-370" data-mp="3podsv0nwd11r">
          <Field label="Category" value={form.category} placeholder="Compliance" onChange={v => setForm(f => ({
          ...f,
          category: v
        }))} data-id="element-371" data-mp="3pn2slatnubfe" />
          <Field label="Call to action" value={form.due} placeholder="Acknowledge by Fri" onChange={v => setForm(f => ({
          ...f,
          due: v
        }))} data-id="element-372" data-mp="1nr3m6qzhoneq" />
        </div>
        <div data-id="element-373" data-mp="1sqg899ijdqg9">
          <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-374" data-mp="1sqokcz6u4rqe">
            Icon
          </span>
          <div className="flex flex-wrap gap-2" data-id="element-375" data-mp="26owobr2agtb2">
            {iconChoices.map(c => {
            const Icon = c.icon;
            const active = form.iconKey === c.key;
            return <button key={c.key} onClick={() => setForm(f => ({
              ...f,
              iconKey: c.key
            }))} aria-label={c.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${active ? 'bg-gold-500 text-navy-900 ring-2 ring-gold-400' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`} data-id="element-376" data-mp="cbx39if536we">
                  <Icon className="w-5 h-5" data-id="element-377" data-mp="7j6x4yj5f2l" />
                </button>;
          })}
          </div>
        </div>
        <div className="flex gap-3 pt-1" data-id="element-378" data-mp="33p1ct6uqsc0d">
          <button onClick={save} className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-379" data-mp="33psurevf2b86">
            {editing === 'new' ? 'Add must-read' : 'Save'}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-navy-600 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors" data-id="element-380" data-mp="ny5aok1i4d6s">
            Cancel
          </button>
        </div>
      </div>;
  }
  return <div data-id="element-381" data-mp="28fxp0rm9qgoy">
      <div className="flex items-center justify-between mb-4" data-id="element-382" data-mp="28g63gny1hu29">
        <div data-id="element-383" data-mp="28gpj1y07w6d4">
          <h3 className="font-serif font-bold text-lg text-navy-900" data-id="element-384" data-mp="28ia4my4k0eyu">
            Must Reads
          </h3>
          <p className="text-sm text-navy-400" data-id="element-385" data-mp="1yq4eltxv47ma">
            Cards shown on the home dashboard.
          </p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-386" data-mp="1trjdlsasdjws">
          <Plus className="w-4 h-4" data-id="element-387" data-mp="1kregqc37y3wv" /> New
        </button>
      </div>
      <ul className="divide-y divide-navy-50" data-id="element-388" data-mp="1fribyamp68g9">
        {mustReads.map(m => {
        const Icon = m.icon;
        return <li key={m.id} className="flex items-center gap-3 py-3" data-id="element-389" data-mp="3f3beems9860d">
              <span className="w-9 h-9 rounded-lg bg-navy-50 flex items-center justify-center text-navy-600 shrink-0" data-id="element-390" data-mp="3a5jasvi8yvgc">
                {Icon ? <Icon className="w-4 h-4" data-id="element-391" data-mp="48re8luhgee7" /> : null}
              </span>
              <div className="min-w-0 flex-1" data-id="element-392" data-mp="3vgdk8l4ocsy8">
                <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-393" data-mp="3vfkmg9np1ds8">
                  {m.title}
                </p>
                <p className="text-xs text-navy-400" data-id="element-394" data-mp="e1f93b1wk8aa">
                  {m.category} · {m.due}
                </p>
              </div>
              <button onClick={() => startEdit(m.id)} className="p-2 text-navy-500 hover:bg-navy-50 rounded-lg" aria-label={`Edit ${m.title}`} data-id="element-395" data-mp="3hft3rvm8f97w">
                <Pencil className="w-4 h-4" data-id="element-396" data-mp="3qh9clyxef3a0" />
              </button>
              <button onClick={() => removeMustRead(m.id)} className="p-2 text-navy-500 hover:bg-red-50 hover:text-red-600 rounded-lg" aria-label={`Delete ${m.title}`} data-id="element-397" data-mp="3mffik9hpkxit">
                <Trash2 className="w-4 h-4" data-id="element-398" data-mp="11pyjbw4iiv50" />
              </button>
            </li>;
      })}
        {mustReads.length === 0 && <li className="py-6 text-center text-sm text-navy-400" data-id="element-399" data-mp="19askx8bv20or">
            No must-reads yet.
          </li>}
      </ul>
    </div>;
}